import { memo, useEffect, useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  FetchBlockItemDimensions,
  fetchBlock,
  getIndicatorTableData,
  getShowNonEditableColumns,
  toggleShowNonEditableColumns,
} from 'redux/PlanPageSlice';
import { getBaseScenario, getSelectedIndicators } from 'redux/PlannerModeSlice';
import { fetchAllScenarios } from 'redux/ScenarioSlice';
import { monthNames } from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/MultiDimensionTableOpzV2.utils';
import { AppDispatch } from 'utils/GlobalHelpers';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../../constants';
import InputPanelTableToolbarV1 from '../components/InputPanelTableToolbarV1';

import BuilderModeMultiDimTableV1 from './BuilderModeMultiDimTableV1';

export interface BuilderModeIndicatorTableV1Properties {
  refetchRequest: () => void;
  isPercentage: string;
}

const isHeaderNonEditable = (
  header: { is_dimension?: boolean; name: string },
  date?: string,
): boolean => {
  if (header.is_dimension) return false;
  if (!date) return false;

  if (header.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(header.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  const headingParts = String(header.name).split('-');
  const scenarioParts = date.split('-');
  if (headingParts.length < 2 || scenarioParts.length < 2) return false;

  const headingYear = Number(headingParts[1]);
  const scenarioYear = Number(scenarioParts[1]);
  if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

  if (headingYear < scenarioYear) return true;
  return (
    headingYear === scenarioYear &&
    monthNames.indexOf(headingParts[0]) <= monthNames.indexOf(scenarioParts[0])
  );
};

const BuilderModeIndicatorTableV1 = memo(
  ({ refetchRequest, isPercentage }: BuilderModeIndicatorTableV1Properties) => {
    const dispatch = useDispatch<AppDispatch>();
    const { modelId, blockId } = useParams();
    const currentDriver = useSelector(getSelectedIndicators);
    const indicatorTableData = useSelector(getIndicatorTableData);
    const showNonEditableColumns = useSelector(getShowNonEditableColumns);
    const baseScenario = useSelector(getBaseScenario);

    const hasAnyNonEditableValueColumns = useMemo(() => {
      if (!indicatorTableData?.column_headers?.length) return false;

      const startDate = new Date(baseScenario?.start_date);
      startDate.setMonth(startDate.getMonth() - 1);
      const yearFormat = startDate.getFullYear();
      const monthFormat = `${monthNames[startDate.getMonth()]}-${String(yearFormat).slice(-2)}`;
      const targetDate = indicatorTableData?.type === 'raw' ? monthFormat : String(yearFormat);

      return indicatorTableData.column_headers.some(
        (header: { is_dimension?: boolean; name: string }) =>
          isHeaderNonEditable(header, targetDate),
      );
    }, [indicatorTableData?.column_headers, indicatorTableData?.type, baseScenario?.start_date]);

    useEffect(() => {
      if (modelId && blockId) {
        dispatch(fetchBlock(Number(blockId)));
        dispatch(FetchBlockItemDimensions({ blockId }));
        dispatch(fetchAllScenarios(Number(modelId)));
      }
    }, [blockId, modelId]);

    if (!currentDriver) {
      return (
        <Flex direction='column' gap='8px' pb={1}>
          {[...new Array(4)].map((_, index) => (
            <Flex key={index} gap='8px' px={2}>
              {[...new Array(4)].map((__, subIndex) => (
                <Box
                  key={subIndex}
                  flex={1}
                  h='35px'
                  borderRadius='8px'
                  bg='rgba(139, 92, 246, 0.15)'
                />
              ))}
            </Flex>
          ))}
        </Flex>
      );
    }

    return (
      <Flex
        direction='column'
        gap={BUILDER_MODE_V1_LAYOUT.inputPanelTableToolbarGap}
        flex={1}
        minH={0}
        h='100%'
        w='100%'
        overflow='hidden'
      >
        <InputPanelTableToolbarV1 indicatorId={currentDriver.id} refetchRequest={refetchRequest} />

        {hasAnyNonEditableValueColumns && (
          <Flex justify='flex-end'>
            <Box
              as='button'
              type='button'
              onClick={() => dispatch(toggleShowNonEditableColumns())}
              px='10px'
              py='3px'
              border='1px solid'
              borderColor={BUILDER_MODE_V1_TOKENS.border}
              borderRadius='6px'
              bg='transparent'
              cursor='pointer'
              _hover={{ bg: BUILDER_MODE_V1_TOKENS.activeBg }}
            >
              <Text fontSize='12px' fontWeight='600' color={BUILDER_MODE_V1_TOKENS.textDark}>
                {showNonEditableColumns ? 'Hide actuals' : 'Show actuals'}
              </Text>
            </Box>
          </Flex>
        )}

        <BuilderModeMultiDimTableV1 isPercentage={isPercentage} refetchRequest={refetchRequest} />
      </Flex>
    );
  },
);

BuilderModeIndicatorTableV1.displayName = 'BuilderModeIndicatorTableV1';

export default BuilderModeIndicatorTableV1;
